import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // One-time cleanup: tokens are no longer persisted in localStorage
    // (auth now uses httpOnly cookies set by the backend).
    localStorage.removeItem("gp_token");
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUser(); }, [loadUser]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, password) => {
    const ref = localStorage.getItem("gp_ref") || undefined;
    // Sponsored-invite token — required to claim a pending sponsor shell.
    let invite;
    try {
      const params = new URLSearchParams(window.location.search);
      invite = params.get("invite") || undefined;
    } catch (err) { console.debug("[auth] invite parse skipped:", err?.message); }
    const res = await api.post("/auth/register", { name, email, password, ref, invite });
    localStorage.removeItem("gp_ref");
    setUser(res.data.user);
    return res.data.user;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (err) { console.debug("[auth] logout swallow:", err?.message); }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, loginWithGoogle, logout, setUser, reload: loadUser }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
